/* eslint-disable radix */
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { CommonPopupWrapperComponent } from 'Components/common/commonPopupWrapperComponent';
import Button from 'Components/common/Button';
import ToolTipWithIcon from 'Components/common/ToolTipWithIcon';
import RadioButton from 'Components/common/RadioBtn';
import { userDetail } from 'Stores/userDetail';
import { Utils } from 'Utils/utils';
import './userDataPopup.scss';

const defaultData = {
  firstName: '',
  lastName: '',
  email: '',
  credits: -1,
};

const UserDataPopupContent = props => {
  const {
    userData,
    setUserData,
    editPersonalFields,
    onSubmit,
    popupBoundary,
    setErrors,
    errors,
  } = props;
  const { lbCredits } = userDetail.userInfo;

  return (
    <form id="user-form" onSubmit={onSubmit}>
      <div className="first-name-wrap">
        <label htmlFor="first-name">
          First Name
          <input
            id="first-name"
            disabled={!editPersonalFields}
            className={errors.firstName ? ' invalid' : ''}
            placeholder="Enter First Name"
            value={userData.firstName}
            onChange={e => {
              setUserData('firstName', e.target.value);
              setErrors({ ...errors, firstName: !e.target.value });
            }}
          />
          <span className="error">Please enter first name</span>
        </label>
      </div>
      <div className="last-name-wrap">
        <label htmlFor="last-name">
          Last Name
          <input
            className={errors.lastName ? ' invalid' : ''}
            id="last-name"
            disabled={!editPersonalFields}
            placeholder="Enter Last Name"
            value={userData.lastName}
            onChange={e => {
              setUserData('lastName', e.target.value);
              setErrors({ ...errors, lastName: !e.target.value });
            }}
          />
          <span className="error">Please enter last name</span>
        </label>
      </div>
      <div className="email-wrap">
        <label htmlFor="email">
          Email ID
          <input
            className={errors.email ? ' invalid' : ''}
            id="email"
            type="email"
            disabled={!editPersonalFields}
            placeholder="Enter Email ID"
            value={userData.email}
            onChange={e => {
              setUserData('email', e.target.value);
              setErrors({ ...errors, email: !e.target.value });
            }}
          />
          <span className="error">Please enter email</span>
        </label>
      </div>
      <div className="credit-limit-wrap">
        <div className="credit-limit-radio-section">
          <div className="credit-limit-title">
            Credit Limit
            <ToolTipWithIcon
              offset={popupBoundary}
              className="credit-limit-tooltip"
            >
              Choose how you want to allocate credits for you team members.
            </ToolTipWithIcon>
          </div>
          <div className="radio-wrap">
            <RadioButton
              id="no-limit"
              checked={userData.credits === -1}
              onChange={() => {
                setUserData('credits', -1);
              }}
              label="No Limits"
              value="No Limits"
            />
            <RadioButton
              id="set-credit-limit"
              checked={userData.credits !== -1}
              onChange={() => {
                setUserData('credits', '');
              }}
              label="Set Credit Limit"
              value="Set Credit Limit"
            />
          </div>
        </div>
        {userData.credits !== -1 && (
          <div className="credits-wrap">
            <label htmlFor="credit-input">
              Maximum credits per month
              <input
                min={0}
                type="number"
                id="credit-input"
                className={errors.credits ? ' invalid' : ''}
                placeholder="Enter Credits"
                value={userData.credits >= 0 ? userData.credits : ''}
                onChange={e => {
                  setUserData('credits', e.target.value);
                  setErrors({ ...errors, credits: !e.target.value });
                }}
              />
              <span className="error">
                {userData.credits > lbCredits
                  ? 'The credit limit value shouldn’t exceed more than your credit balance'
                  : 'Please enter credits'}
              </span>
            </label>
          </div>
        )}
      </div>
    </form>
  );
};

const FooterSection = observer(props => {
  const { type, onClose, isEdited } = props;
  const { lbCredits } = userDetail.userInfo;

  return (
    <div className="footer">
      <div className="credit-section">
        <span className="available-credits">
          {Utils.numberFormat(lbCredits)} Credits
        </span>
        <span className="credits-text">
          Available in your {Utils.getPlanType()} plan
        </span>
      </div>
      <div className="button-wrap">
        <Button
          disabled={!isEdited}
          form="user-form"
          btnText="Cancel"
          icon={false}
          type="button"
          className="cancel"
          click={onClose}
        />
        <Button
          disabled={!isEdited}
          form="user-form"
          btnText={type === 'ADD' ? 'Invite User' : 'Save'}
          icon={false}
          type="submit"
          className="primary-btn"
        />
      </div>
    </div>
  );
});

const UserDataPopup = observer(props => {
  const { title, onClose, type, data, onDataSubmit } = props;

  const { lbCredits } = userDetail.userInfo;
  const defaultValues = Object.keys(data).length > 0 ? data : defaultData;
  const [userData, updateUserData] = useState(defaultValues);
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    credits: false,
  });
  const popupRef = useRef();
  const [popupBoundary, setPopupBoundary] = useState({ left: 0, right: 0 });
  const isEdited = Object.keys(defaultValues).some(
    key => defaultValues[key] !== userData[key],
  );
  useEffect(() => {
    const boundary = popupRef.current.getBoundingClientRect();
    setPopupBoundary({
      left: boundary.left,
      right: window.innerWidth - boundary.right,
    });
  }, []);

  const setUserData = (label, value) => {
    updateUserData({ ...userData, [label]: value });
  };

  const onSubmit = e => {
    e.preventDefault();
    const fieldErrors = {};
    const isValid = Object.keys(userData).every(field => {
      const isFieldInvalid =
        field === 'credits'
          ? parseInt(userData[field]) > lbCredits ||
            Number.isNaN(parseInt(userData[field])) ||
            parseInt(userData[field]) < -1
          : type === 'ADD'
          ? !userData[field]
          : false;
      fieldErrors[field] = isFieldInvalid;
      return !isFieldInvalid;
    });
    setErrors(fieldErrors);
    if (isValid) onDataSubmit(userData);
  };

  return (
    <CommonPopupWrapperComponent
      popupRef={popupRef}
      showCloseBtn
      titleText={title}
      content={
        <UserDataPopupContent
          errors={errors}
          setErrors={setErrors}
          popupBoundary={popupBoundary}
          userData={userData}
          setUserData={setUserData}
          editPersonalFields={type === 'ADD'}
          onSubmit={onSubmit}
        />
      }
      actionBtn={
        <FooterSection type={type} onClose={onClose} isEdited={isEdited} />
      }
      customParentClass="user-popup"
      closeClickCallback={onClose}
      clickOutsidePopupCallback={onClose}
    />
  );
});

export default UserDataPopup;
